import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PeopleMap from "./page.jsx";
import { useLanguage } from "./LanguageContext";
import { Translator } from './translator';

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

export default function FriendRequests() {
  const navigate = useNavigate();
  const me = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  const { lang, setLang } = useLanguage();
  const { translateText } = Translator();
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requests"); // "requests" | "find"
  const [search, setSearch] = useState("");
  const [requestsTab, setRequestsTab] = useState("incoming"); // "incoming" | "outgoing"
  const [profiles, setProfiles] = useState({});   // cache user profiles by email (for requests lists)
  const [openFriendMenuFor, setOpenFriendMenuFor] = useState(null);   // 3-dots menu

  // Static copy for it / en / fr
  const baseTextByLang = {
    it: {
      text1: "TecheNetwork",
      text2: "Accedi per vedere amici e richieste.",
      text3: "Richieste di amicizia",
      text4: "Trova Amici",
      text5: "Nuove richieste",
      text6: "Richieste inviate",
      text7: "Caricamento…",
      text8: "Nessuna richiesta di amicizia in sospeso.",
      textUtente: "Utente",
      textConfirm: "Conferma",
      textDelete: "Elimina",
      text9: "Non ci sono nuove richieste di amicizia.",
      text10: "Non hai ancora inviato richieste.",
      text11: "Annulla richiesta",
      text12: "I tuoi amici",
      text13: "Caricamento della lista amici…",
      text14: "Non hai ancora amici.",
      text15: "Nessun amico corrisponde a questa ricerca.",
      text16: "Cerca amici",
      text17: "Cerca amici per nome",
      text18: "Rimuovi dagli amici",
      text19: "Annulla amicizia",
      text20: "",
    },
    en: {
      text1: "TecheNetwork",
      text2: "Log in to see friends and requests.",
      text3: "Friend requests",
      text4: "Find Friends",
      text5: "New requests",
      text6: "Sent requests",
      text7: "Loading…",
      text8: "No pending friend requests.",
      textUtente: "User",
      textConfirm: "Confirm",
      textDelete: "Delete",
      text9: "No new friend requests.",
      text10: "You have not sent any requests yet.",
      text11: "Cancel request",
      text12: "Your Friends",
      text13: "Loading friends list…",
      text14: "You have no friends yet.",
      text15: "No friends match this search.",
      text16: "Search friends",
      text17: "Search friends by name",
      text18: "Unfriend",
      text19: "Cancel Friendship",
      text20: "",
    },
    fr: {
      text1: "TecheNetwork",
      text2: "Connectez-vous pour voir vos amis et vos demandes.",
      text3: "Demandes d’ami",
      text4: "Trouver des amis",
      text5: "Nouvelles demandes",
      text6: "Demandes envoyées",
      text7: "Chargement…",
      text8: "Aucune demande d’ami en attente.",
      textUtente: "Utilisateur",
      textConfirm: "Confirmer",
      textDelete: "Supprimer",
      text9: "Aucune nouvelle demande d’ami.",
      text10: "Vous n’avez pas encore envoyé de demandes.",
      text11: "Annuler la demande",
      text12: "Vos amis",
      text13: "Chargement de la liste d’amis…",
      text14: "Vous n’avez pas encore d’amis.",
      text15: "Aucun ami ne correspond à cette recherche.",
      text16: "Rechercher des amis",
      text17: "Rechercher des amis par nom",
      text18: "Retirer des amis",
      text19: "Annuler l’amitié",
      text20: "",
    },
  };

  const activeBaseText = baseTextByLang[lang] || baseTextByLang.it;
  const [translatedText, setTranslatedText] = useState(activeBaseText);

  // Static for it/en/fr, Translator fallback for all other languages
  useEffect(() => {
    let cancelled = false;

    // Direct static strings for Italian / English / French
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang] || baseTextByLang.it);
      return;
    }

    // Other languages → use Translator starting from Italian text
    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);

        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            if (!value) return [key, value];
            const out = await translateText(value);
            return [key, out || value]; // fallback to Italian string
          })
        );

        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch (err) {
        if (!cancelled) {
          setTranslatedText(baseTextByLang.it);
        }
      }
    };

    translateAll();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLang]);



  // Load display names for all emails appearing in incoming/outgoing requests
  async function loadProfilesForRequests(incomingArr, outgoingArr) {
    const emails = new Set();

    (incomingArr || []).forEach((r) => {
      if (r?.from) emails.add(r.from.toLowerCase());
    });
    (outgoingArr || []).forEach((r) => {
      if (r?.to) emails.add(r.to.toLowerCase());
    });

    if (emails.size === 0) {
      setProfiles({});
      return;
    }

    const entries = await Promise.all(
      Array.from(emails).map(async (email) => {
        try {
          const res = await fetch(
            `${BASE}/getUserID?email=${encodeURIComponent(email)}`
          );
          const json = await res.json();

          // Try to normalize different possible shapes from backend
          let props = null;
          if (Array.isArray(json) && json[0]?.n?.properties) {
            props = json[0].n.properties;
          } else if (json?.n?.properties) {
            props = json.n.properties;
          } else if (json?.properties) {
            props = json.properties;
          } else {
            props = json;
          }

          const displayName =
            (props?.uName ||
              props?.firstName ||
              props?.lastName ||
              "Utente") + "";

          return [email, { displayName }];
        } catch (e) {
          console.error("getUserID failed for", email, e);
          return [email, { displayName: email }];
        }
      })
    );

    const map = {};
    for (const [email, profile] of entries) {
      map[email] = profile;
    }
    setProfiles(map);
  }


  async function load() {
    if (!me?.email) return;

    setLoading(true);

    // Requests
    const r = await fetch(
      `${BASE}/friends_requests?email=${encodeURIComponent(me.email)}`
    )
      .then((x) => x.json())
      .catch(() => ({ incoming: [], outgoing: [] }));

    setIncoming(r.incoming || []);
    setOutgoing(r.outgoing || []);

    // pre-load display names for request senders/receivers
    await loadProfilesForRequests(r.incoming || [], r.outgoing || []);

    // let the rest of the app (Header) know how many incoming requests we have
    const incomingCount = (r.incoming || []).length;
    window.dispatchEvent(
      new CustomEvent("friends:updated", {
        detail: { incomingCount },
      })
    );

    // Friends
    const f = await fetch(
      `${BASE}/friends_list?email=${encodeURIComponent(me.email)}`
    )
      .then((x) => x.json())
      .catch(() => []);

    setFriends(f || []);

    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setOpenFriendMenuFor(null);
      }
    }

    if (openFriendMenuFor) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [openFriendMenuFor]);




  async function removeFriend(otherEmail) {
    if (!otherEmail) return;
    try {
      await fetch(`${BASE}/friends_unfriend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ me: me.email, other: otherEmail }),
      });
      await load();
    } catch (e) {
      console.error("friends_unfriend failed", e);
    } finally {
      setOpenFriendMenuFor(null);
    }
  }



  async function accept(other) {
    const res = await fetch(`${BASE}/friends_accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ me: me.email, other }),
    }).then((r) => r.json());

    if (res?.status === "ok") {
      window.dispatchEvent(
        new CustomEvent("friends:accepted", { detail: { convId: res.convId } })
      );
      navigate(`/messages?convId=${encodeURIComponent(res.convId)}`);
    }
    await load();
  }

  async function decline(other) {
    await fetch(`${BASE}/friends_decline`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ me: me.email, other }),
    });
    await load();
  }

  async function cancelRequest(other) {
    await fetch(`${BASE}/friends_cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ me: me.email, other }),
    });
    await load();
  }


  if (!me?.email) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4">{translatedText.text1}</h1>
        <div className="text-sm text-gray-600">
          {translatedText.text2}
        </div>
      </div>
    );
  }

  // ----- SEARCH: filter -----
  const normalizedSearch = search.trim().toLowerCase();

  const filteredFriends =
    normalizedSearch === ""
      ? friends
      : friends.filter((f) => {
          const displayName = (f.uName || f.name || "").toString().toLowerCase();
          return displayName.includes(normalizedSearch);
        });

  return (
    <>
      {/* overlay to catch outside clicks when a friend menu is open */}
      {openFriendMenuFor && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setOpenFriendMenuFor(null)}
          aria-hidden="true"
        />
      )}

    <div className="max-w-5xl mx-auto px-4 py-6">

      {/* TOP ROW: title + tabs */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h1 className="text-2xl font-bold">{translatedText.text1}</h1>

        {/* Tabs */}
        <div className="inline-flex bg-gray-100 rounded-full p-1 text-sm font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab("requests")}
            className={`px-4 py-1 rounded-full transition 
              ${
                activeTab === "requests"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
          >
            {translatedText.text3}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("find")}
            className={`px-4 py-1 rounded-full transition 
              ${
                activeTab === "find"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
          >
            {translatedText.text4}
          </button>
        </div>
      </div>




      {/* ------------ TAB PANEL AREA ------------ */}
      {activeTab === "requests" && (
        <section className="mb-10">
          <h2 className="text-lg text-black font-semibold mb-3">
            {translatedText.text3}
          </h2>


          {/* Inner tabs: New requests / Sent requests */}
          <div className="inline-flex bg-gray-100 rounded-full p-1 text-xs sm:text-sm font-semibold mb-4">
            <button
              type="button"
              onClick={() => setRequestsTab("incoming")}
              className={`px-3 sm:px-4 py-1 rounded-full transition ${
                requestsTab === "incoming"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              {translatedText.text5}
            </button>
            <button
              type="button"
              onClick={() => setRequestsTab("outgoing")}
              className={`px-3 sm:px-4 py-1 rounded-full transition ${
                requestsTab === "outgoing"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              {translatedText.text6}
            </button>
          </div>


          {loading && (
            <div className="text-sm text-gray-500 mb-4">{translatedText.text7}</div>
          )}

          {!loading &&
            requestsTab === "incoming" &&
            incoming.length === 0 &&
            outgoing.length === 0 && (
              <div className="text-sm text-gray-500">
                {translatedText.text8}
              </div>
          )}

          {/* Show only the selected inner tab */}
          {!loading && requestsTab === "incoming" && incoming.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {translatedText.text5}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {incoming.map((r, i) => {
                  const emailKey = (r.from || "").toLowerCase();
                  const fromProfile = profiles[emailKey] || {};
                  const fromLabel = fromProfile.displayName || translatedText.textUtente;

                  return (
                    <div
                      key={`in-${i}`}
                      className="flex items-center p-3 border rounded-xl bg-white shadow-sm"
                    >
                      <img
                        src="/avatar.png"
                        alt=""
                        className="w-14 h-14 rounded-full object-cover mr-3"
                      />

                      <div className="flex-1">
                        {/* top row: name + actions */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-gray-900">
                            {fromLabel}
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={() => accept(r.from)}
                              className="px-3 py-1 text-xs sm:text-sm bg-blue-600 text-white rounded-full"
                            >
                              {translatedText.textConfirm}
                            </button>
                            <button
                              onClick={() => decline(r.from)}
                              className="px-3 py-1 text-xs sm:text-sm bg-gray-200 rounded-full"
                            >
                              {translatedText.textDelete}
                            </button>
                          </div>
                        </div>

                        {/* second row: timestamp */}
                        <div className="text-xs text-gray-500 mt-1">
                          {r.ts ? new Date(r.ts).toLocaleString() : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {incoming.length === 0 && (
                <div className="text-sm text-gray-500 mt-2">
                  {translatedText.text9}
                </div>
              )}
            </div>
          )}

          {!loading && requestsTab === "outgoing" && (
            <div>
              {outgoing.length === 0 && (
                <div className="text-sm text-gray-500">
                  {translatedText.text10}
                </div>
              )}

              {outgoing.map((r, i) => {
                const emailKey = (r.to || "").toLowerCase();
                const toProfile = profiles[emailKey] || {};
                const toLabel = toProfile.displayName || translatedText.textUtente;

                return (
                  <div
                    key={`out-${i}`}
                    className="flex items-center p-3 border rounded-xl bg-white shadow-sm"
                  >
                    <img
                      src="/avatar.png"
                      alt=""
                      className="w-14 h-14 rounded-full object-cover mr-3"
                    />
                    <div className="flex-1">
                      {/* top row: name + action */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-gray-900">
                          {toLabel}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => cancelRequest(r.to)}
                            className="px-3 py-1 text-xs sm:text-sm bg-gray-200 rounded-full"
                          >
                            {translatedText.text11}
                          </button>
                        </div>
                      </div>

                      {/* second row: timestamp */}
                      <div className="text-xs text-gray-500 mt-1">
                        {r.ts ? new Date(r.ts).toLocaleString() : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {activeTab === "find" && (
        <section className="mb-10 -mx-4 sm:-mx-6 lg:mx-0">
          {/* PeopleMap fills its own width/hero; we just embed it here */}
          <PeopleMap />
        </section>
      )}

      {/* ------------ FRIENDS LIST ------------ */}
      <section>
        <h2 className="text-lg text-black font-semibold mb-3">{translatedText.text12}</h2>

        {loading && friends.length === 0 && (
          <div className="text-sm text-gray-500">{translatedText.text13}</div>
        )}

        {!loading && filteredFriends.length === 0 && (
          <div className="text-sm text-gray-500">
            {friends.length === 0
              ? translatedText.text14
              : translatedText.text15}
          </div>
        )}



        {/* SEARCH */}
        {activeTab === "requests" && (
          <div className="mb-6">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={translatedText.text16}
              className="w-full max-w-md px-4 py-2 rounded-full bg-gray-100 border border-gray-300 text-sm"
              aria-label={translatedText.text17}
            />
          </div>
        )}

        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filteredFriends.map((f, i) => {
            const displayName = f.uName || f.name || translatedText.textUtente;
            const imgSrc = (() => {
              const raw = f.image || "";
              if (!raw) return "/avatar.png";
              if (/^https?:\/\//i.test(raw)) return raw;
              return `${BASE}/media/${raw.replace(/^\/+/, "")}`;
            })();

            const locationLabel = [f.city, f.country].filter(Boolean).join(" · ");

            return (
              <div
                key={i}
                className="relative flex items-center p-3 border rounded-xl bg-white shadow-sm"
              >
                <img
                  src={imgSrc}
                  alt=""
                  className="w-14 h-14 rounded-full object-cover mr-3"
                />

                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {displayName}
                  </div>

                  {locationLabel && (
                    <div className="text-xs text-gray-500">
                      {locationLabel}
                    </div>
                  )}
                </div>

                {/* 3-dots button */}
                <button
                  type="button"
                  onClick={() =>
                    setOpenFriendMenuFor(
                      openFriendMenuFor === f.email ? null : f.email
                    )
                  }
                  className="px-2 py-1 text-lg text-gray-500 hover:bg-gray-100 rounded-full"
                >
                  ⋯
                </button>

                {/* Dropdown menu */}
                {openFriendMenuFor === f.email && (
                  <div className="absolute right-2 top-12 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg z-20 text-sm">
                    <button
                      type="button"
                      onClick={() => removeFriend(f.email)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 rounded-t-xl"
                    >
                      {translatedText.text18}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeFriend(f.email)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 rounded-b-xl"
                    >
                      {translatedText.text19}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
    </>
  );
}
